import {
  formatDateMMddYYYY,
  groupBy,
  MetricDataExtractor,
  MetricProgressBar,
  toProgressSegments,
} from '../utils/utils';
import {
  ProgressPortraitMetrics,
  Psi4Assessment,
  PssAssessment,
  SipaAssessment,
} from '../model/ProgressPortrait';
import { isEmpty, uniq } from 'lodash';
import { CellData, NA_CELL, ProgressBar } from './ProgressBar';
import { CellTooltip, TooltipItem } from './CellTooltip';

const extractor: MetricDataExtractor<
  PssAssessment | Psi4Assessment | SipaAssessment
> = (progressPortraitMetrics: ProgressPortraitMetrics) => {
  const psss = progressPortraitMetrics.psss;
  const psi = progressPortraitMetrics.psi4s;
  const sipa = progressPortraitMetrics.sipas;
  const psssGroupByDate = groupBy(psss, (pss) =>
    pss.assessment_Date__c.valueOf(),
  );
  const sipaGroupByDate = groupBy(sipa, (sipa) =>
    sipa.SIPA_Assessment_Date__c.valueOf(),
  );
  const psiGroupByDate = groupBy(psi, (psi) =>
    psi.assessment_Date__c.valueOf(),
  );
  const sortedDate = uniq([
    ...psssGroupByDate.keys(),
    ...sipaGroupByDate.keys(),
    ...psiGroupByDate.keys(),
  ]).sort();
  const metricData = sortedDate.map((date) => {
    const pssData = psssGroupByDate.get(date) ?? [];
    const sipaData = sipaGroupByDate.get(date) ?? [];
    const psiData = psiGroupByDate.get(date) ?? [];
    if (!isEmpty(pssData)) {
      return pssData;
    }
    if (!isEmpty(sipaData)) {
      return sipaData;
    }
    return psiData;
  });
  return metricData.reduce((result, assessments) => {
    return [...result, ...assessments];
  }, [] as Array<PssAssessment | Psi4Assessment | SipaAssessment>);
};

const isPsiAssessment = (
  pssData: PssAssessment | Psi4Assessment | SipaAssessment,
): pssData is Psi4Assessment => {
  return 'parental_Distress__c' in pssData;
};

const isSipaAssessment = (
  pssData: PssAssessment | Psi4Assessment | SipaAssessment,
): pssData is SipaAssessment => {
  return 'SIPA_Assessment_Date__c' in pssData;
};

const toCellData = (
  pssData: PssAssessment | Psi4Assessment | SipaAssessment,
): CellData => {
  if (isPsiAssessment(pssData)) {
    return toPsiCellData(pssData);
  }
  if (isSipaAssessment(pssData)) {
    return toSipaCellData(pssData);
  }
  return toPssCellData(pssData);
};

const toPssCellData = (pssData: PssAssessment): CellData => {
  const tooltip = <PsssTooltip psss={pssData} />;
  if (pssData.total_Stress__c >= 18 && pssData.total_Stress__c <= 49) {
    return {
      level: 'high',
      label: 'Average',
      tooltip,
    };
  }
  if (pssData.total_Stress__c >= 50 && pssData.total_Stress__c <= 61) {
    return {
      level: 'medium',
      label: 'Moderately High',
      tooltip,
    };
  }
  if (pssData.total_Stress__c >= 62) {
    return {
      level: 'low',
      label: 'High',
      tooltip,
    };
  }
  return NA_CELL;
};

const toPsiCellData = (psiData: Psi4Assessment): CellData => {
  const tooltip = <PsiTooltip psi={psiData} />;
  if (psiData.total_Stress__c >= 16 && psiData.total_Stress__c <= 84) {
    return {
      level: 'high',
      label: 'Normal',
      tooltip,
    };
  }
  if (psiData.total_Stress__c >= 85 && psiData.total_Stress__c <= 89) {
    return {
      level: 'medium',
      label: 'High',
      tooltip,
    };
  }
  if (psiData.total_Stress__c >= 90) {
    return {
      level: 'low',
      label: 'Clinically Significant',
      tooltip,
    };
  }
  return NA_CELL;
};

const toSipaCellData = (sipaData: SipaAssessment): CellData => {
  const tooltip = <SipaTooltip sipa={sipaData} />;
  if (sipaData.index_of_Total_Parenting_Stress__c < 85) {
    return {
      level: 'high',
      label: 'Within Normal Limits',
      tooltip,
    };
  }
  if (
    sipaData.index_of_Total_Parenting_Stress__c >= 85 &&
    sipaData.index_of_Total_Parenting_Stress__c <= 89
  ) {
    return {
      level: 'medium',
      label: 'Borderline',
      tooltip,
    };
  }
  if (
    sipaData.index_of_Total_Parenting_Stress__c >= 90 &&
    sipaData.index_of_Total_Parenting_Stress__c <= 95
  ) {
    return {
      level: 'low',
      label: 'Clinically Severe',
      tooltip,
    };
  }
  if (sipaData.index_of_Total_Parenting_Stress__c > 95) {
    return {
      level: 'low',
      label: 'Clinically Significant',
      tooltip,
    };
  }
  return NA_CELL;
};

export const ParentStressMetric = ({
  progressPortraitMetrics,
}: MetricProgressBar) => {
  const segments = toProgressSegments(
    progressPortraitMetrics,
    extractor,
    toCellData,
  );
  return <ProgressBar segments={segments} />;
};

const PsssTooltip = ({ psss }: { psss: PssAssessment }) => {
  let text = 'N/A';

  if (psss.total_Stress__c >= 18 && psss.total_Stress__c <= 49) {
    text = 'Average';
  } else if (psss.total_Stress__c >= 50 && psss.total_Stress__c <= 61) {
    text = 'Moderately High';
  } else if (psss.total_Stress__c >= 62) {
    text = 'High';
  }
  const info: TooltipItem[] = [
    {
      label: 'PSS assessment date:',
      value: formatDateMMddYYYY(psss.assessment_Date__c),
    },
    {
      label: 'Stress Score:',
      value: `${psss.total_Stress__c} (${text}) `,
    },
    {
      label: 'Respondent:',
      value: psss.name_Of_Respondent__c,
    },
  ];
  return <CellTooltip info={info} />;
};

const PsiTooltip = ({ psi }: { psi: Psi4Assessment }) => {
  let text = 'N/A';

  if (psi.total_Stress__c >= 16 && psi.total_Stress__c <= 84) {
    text = 'Normal';
  } else if (psi.total_Stress__c >= 85 && psi.total_Stress__c <= 89) {
    text = 'High';
  } else if (psi.total_Stress__c >= 90) {
    text = 'Clinically Significant';
  }
  const info: TooltipItem[] = [
    {
      label: 'PSI-4 Date:',
      value: formatDateMMddYYYY(psi.assessment_Date__c),
    },
    {
      label: 'Stress Score:',
      value: `${psi.total_Stress__c} (${text})`,
    },
    {
      label: 'Respondent:',
      value: psi.name_Of_Respondent__c,
    },
  ];
  return <CellTooltip info={info} />;
};

const SipaTooltip = ({ sipa }: { sipa: SipaAssessment }) => {
  let text = 'N/A';
  if (sipa.index_of_Total_Parenting_Stress__c < 85) {
    text = 'Within Normal Limits';
  } else if (
    sipa.index_of_Total_Parenting_Stress__c >= 85 &&
    sipa.index_of_Total_Parenting_Stress__c <= 89
  ) {
    text = 'Borderline';
  } else if (
    sipa.index_of_Total_Parenting_Stress__c >= 90 &&
    sipa.index_of_Total_Parenting_Stress__c <= 95
  ) {
    text = 'Clinically Severe';
  } else if (sipa.index_of_Total_Parenting_Stress__c > 95) {
    text = 'Clinically Significant';
  }

  const info: TooltipItem[] = [
    {
      label: 'SIPA Date:',
      value: formatDateMMddYYYY(sipa.SIPA_Assessment_Date__c),
    },
    {
      label: 'Stress Score:',
      value: `${sipa.index_of_Total_Parenting_Stress__c} (${text})`,
    },
    {
      label: 'Respondent:',
      value: sipa.name_Of_Respondent__c,
    },
  ];
  return <CellTooltip info={info} />;
};
